import { useState, useEffect, useRef } from "react"
import TextField from "@mui/material/TextField"
import IconButton from "@mui/material/IconButton"
import Box from "@mui/material/Box"
import Tooltip from "@mui/material/Tooltip"
import RemoveIcon from "@mui/icons-material/Remove"
import AddIcon from "@mui/icons-material/Add"

const QuantitySelector = ({ initialValue = 0, min = 0, max = 999, onChange, onBlur, onAddToBasket, index, row,
    inputLabel = "Quantity", decreaseLabel = "Decrease quantity", increaseLabel = "Increase quantity", showActionTooltips = false }) => {
    const [value, setValue] = useState(initialValue)
    const [isFocused, setIsFocused] = useState(false)
    const inputRef = useRef(null)

    // Store the last value sent to the backend to avoid duplicate calls
    const lastSubmittedValue = useRef(initialValue)

    // Update value when initialValue changes (e.g., from parent component)
    useEffect(() => {
        if (!isFocused) {
            setValue(initialValue)
            lastSubmittedValue.current = initialValue
        }
    }, [initialValue, isFocused])

    const handleIncrement = () => {
        if (value < max) {
            const newValue = value + 1
            setValue(newValue)
            // For buttons, we want immediate updates
            if (onChange) {
                onChange(newValue)
                lastSubmittedValue.current = newValue
            }
        }
    }

    const handleDecrement = () => {
        if (value > min) {
            const newValue = value - 1
            setValue(newValue)
            if (onChange) {
                onChange(newValue)
                lastSubmittedValue.current = newValue
            }
        }
    }

    const handleInputChange = (e) => {
        // Important: Allow explicit "0" input
        const inputValue = e.target.value

        // Allow empty string for better UX when typing
        if (inputValue === "") {
            setValue("")
            return
        }

        // Special case for "0" input
        if (inputValue === "0") {
            setValue(0)
            return
        }

        // Only allow numeric input
        if (/^\d*$/.test(inputValue)) {
            const newValue = Number.parseInt(inputValue, 10)
            if (!isNaN(newValue)) {
                setValue(newValue)
                // Don't call onChange here, wait for blur or Enter
            }
        }
    }

    const handleFocus = (e) => {
        setIsFocused(true)
        // Select all text when focused
        e.target.select()
    }

    const submitValue = () => {
        // If the field is empty, reset to min value
        if (value === "") {
            setValue(min)
            if (lastSubmittedValue.current !== min && onChange) {
                onChange(min)
                lastSubmittedValue.current = min
            }
        } else {
            // Ensure value is within bounds
            const numValue = typeof value === "string" ? Number.parseInt(value, 10) : value
            const clampedValue = Math.min(Math.max(numValue, min), max)
            setValue(clampedValue)

            // Only call onChange if the value has actually changed
            if (lastSubmittedValue.current !== clampedValue && onChange) {
                onChange(clampedValue)
                lastSubmittedValue.current = clampedValue
            }
        }
    }

    const handleBlurEvent = () => {
        setIsFocused(false)
        submitValue()
        if (onBlur) {
            onBlur()
        }
    }

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            e.preventDefault()
            submitValue()
            inputRef.current.blur() // Trigger blur to validate
            if (onAddToBasket) {
                onAddToBasket()
            }
        }
    }

    // The wrapper lets disabled boundary actions explain themselves on hover as well.
    const withTooltip = (button, label) => showActionTooltips
        ? <Tooltip title={label} arrow><span style={{display: "inline-flex"}}>{button}</span></Tooltip> : button

    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            {withTooltip(<IconButton size="small" onClick={handleDecrement} disabled={value <= min} aria-label={decreaseLabel}>
                <RemoveIcon sx={{ fontSize: "16px" }} />
            </IconButton>, decreaseLabel)}

            <TextField
                variant="standard"
                size="small"
                type="text"
                value={value}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlurEvent}
                onKeyDown={handleKeyPress} // Changed from onKeyPress which is deprecated
                inputRef={inputRef}
                inputProps={{
                    "aria-label": inputLabel,
                    min: min,
                    max: max,
                    style: {
                        textAlign: "center",
                        width: "30px",
                        padding: "8.5px 8px",
                        fontSize: "14px",
                    },
                }}
                sx={{
                    "& .MuiInput-underline:before": {
                        borderBottom: "none", // Remove the underline in normal state
                    },
                    "& .MuiInput-underline:after": {
                        borderBottom: "none", // Remove the underline in focused state
                    },
                    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                        borderBottom: "none", // Remove the underline on hover
                    },
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                    },
                }}
            />

            {withTooltip(<IconButton size="small" onClick={handleIncrement} disabled={value >= max} aria-label={increaseLabel}>
                <AddIcon sx={{ fontSize: "16px" }} />
            </IconButton>, increaseLabel)}
        </Box>
    )
}

export default QuantitySelector
